// =============================================================================
// io/sync/commissions.ts — the per-firm commission rate schedules.
// -----------------------------------------------------------------------------
// One row per (user, firm), each carrying a rates map keyed by ROOT symbol.
// Small and user-scoped; the old client loads the whole table with everything
// else on boot.
//
// ── 🚨 A FAILED READ MUST THROW ──
//
// Same shape as accounts-without-adjustments and an empty contract catalogue,
// and worse than either in one respect: the failure is FAVOURABLE. With no
// schedules, `applyCommission` finds no rate, leaves the stored (usually zero)
// fee alone, and every affected trade reports a net P&L that is better than the
// trader's real one — across the KPI strip, the equity curve and every prop
// compliance number that reads from net.
//
// Nothing about that looks broken. It just reads as a slightly better week.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { CommissionSchedule, CommissionSchedules } from '../../domain/commissions';
import { PAGE_ROWS } from './load';

export const COMMISSIONS_SELECT = 'firm_key,firm_display_name,rates';

export function schedulesFromDb(rows: readonly Record<string, unknown>[]): CommissionSchedules {
  const out: Record<string, CommissionSchedule> = {};
  for (const r of rows) {
    const key = String(r['firm_key'] ?? '');
    if (!key) continue;
    // ⚠ Rates arrive as JSONB. Values may be numbers OR numeric strings
    // depending on how they were written, and a string rate multiplies into
    // string concatenation rather than arithmetic — so each is coerced here
    // rather than at the multiply.
    const raw = (r['rates'] ?? {}) as Record<string, unknown>;
    const rates: Record<string, number> = {};
    for (const [sym, v] of Object.entries(raw)) {
      const n = Number(v);
      if (Number.isFinite(n)) rates[sym.trim().toUpperCase()] = n;
    }
    out[key] = { firm: String(r['firm_display_name'] ?? key), rates };
  }
  return out;
}

export async function loadCommissionSchedules(sb: SupabaseClient): Promise<CommissionSchedules> {
  const { data, error } = await sb.from('commission_schedules').select(COMMISSIONS_SELECT);

  if (error) {
    throw new Error(
      `loadCommissionSchedules: ${error.message}. Refusing to continue without them — ` +
        `fee-less imported trades would keep their stored $0 commission, and every net ` +
        `P&L they feed would read BETTER than the trader's real result.`,
    );
  }

  const rows = (data ?? []) as unknown as Record<string, unknown>[];
  if (rows.length >= PAGE_ROWS) {
    throw new Error(
      `loadCommissionSchedules: got ${rows.length} rows, at or above the ${PAGE_ROWS}-row ` +
        `server cap. This read is unpaged because a user has a handful of prop firms; at ` +
        `this size it is being truncated and some accounts would silently lose their rates.`,
    );
  }

  return schedulesFromDb(rows);
}
